/**
 * Phase 21.4 Batch 3C — 🏛 Admin · Hall of Champions viewer
 *
 * Read-only. Uses public GET /api/hall-of-champions + facets endpoints
 * with admin-only Bearer to see everything even when the feature flag
 * is OFF for the public.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import HoFBackfillModal from "@/components/contests/HoFBackfillModal";
import HoFImportSpotlightModal from "@/components/contests/HoFImportSpotlightModal";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { optimizeImage } from "@/lib/mediaUrls";

const positionLabel = (p) => (p === 1 ? "🥇 1st" : p === 2 ? "🥈 2nd" : p === 3 ? "🥉 3rd" : `#${p}`);

const AdminHallOfChampionsPage = () => {
  const [facets, setFacets] = useState({ years: [], contests: [], entry_types: [] });
  const [rows, setRows] = useState([]);
  const [enabled, setEnabled] = useState(null);
  const [year, setYear] = useState("");
  const [contestId, setContestId] = useState("");
  const [entryType, setEntryType] = useState("");
  const [position, setPosition] = useState("");
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairBadgesBusy, setRepairBadgesBusy] = useState(false);

  const startEdit = (row) => {
    setEditingRow({
      hof_id: row.hof_id,
      user_id: row.user_id,
      title: row.contest?.title || row.contest_title || "",
      category: row.category || "weekly_spotlight",
      placement: row.position,
      award_label: row.award_label || "",
      entry_type: row.entry_type || "track",
      entry_ref_id: row.entry_ref_id || "",
      entry_title: row.entry_snapshot?.title || "",
      entry_cover: row.winner_cover_url || row.entry_snapshot?.cover_art || "",
      badge_ids: (row.badges || []),
      awarded_at: row.awarded_at || "",
      banner_image: row.banner_image || row.contest?.banner_image || "",
      notes: row.notes || "",
      contest_id: row.contest_id || "",
      contest_title: row.contest_title || "",
      contest_slug: row.contest_slug || "",
    });
    setAddOpen(true);
  };

  const deleteRow = async (hof_id) => {
    if (!window.confirm("Remove this Hall of Champions row? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/hall-of-champions/${hof_id}`);
      toast.success("Removed.");
      setRows((rs) => rs.filter((r) => r.hof_id !== hof_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const loadFacets = useCallback(async () => {
    try {
      const r = await api.get("/hall-of-champions/facets");
      if (r.data?.enabled === false) {
        setEnabled(false);
      } else {
        setEnabled(true);
      }
      setFacets({
        years: r.data?.years || [],
        contests: r.data?.contests || [],
        entry_types: r.data?.entry_types || [],
      });
    } catch (e) {
      toast.error("Failed to load facets");
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (year) params.year = year;
      if (contestId) params.contest_id = contestId;
      if (entryType) params.entry_type = entryType;
      if (position) params.position = position;
      const r = await api.get("/hall-of-champions", { params });
      if (r.data?.enabled === false) setEnabled(false);
      else setEnabled(true);
      setRows(r.data?.champions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load champions");
    } finally {
      setLoading(false);
    }
  }, [year, contestId, entryType, position]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => { loadRows(); }, [loadRows]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-hof-page">
      <SharedNavigation />
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-3xl font-black">🏛 Hall of Champions</h1>
            <p className="text-sm text-gray-400">Read-only archive of every contest winner.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => { setEditingRow(null); setAddOpen(true); }}
              className="bg-white text-black hover:bg-amber-200 font-black text-xs px-3 py-1 h-auto"
              data-testid="admin-hof-add-btn"
            >
              + Add historical winner
            </Button>
            <Button
              onClick={() => setImportOpen(true)}
              variant="outline"
              className="border-purple-500/40 text-purple-200 hover:bg-purple-500/10 text-xs px-3 py-1 h-auto"
              data-testid="admin-hof-import-btn"
            >
              📥 Import from Spotlight
            </Button>
            <Button
              onClick={async () => {
                if (repairBusy) return;
                setRepairBusy(true);
                try {
                  const r = await api.post("/admin/hall-of-champions/repair-covers");
                  const d = r.data || {};
                  const n = d.count || 0;
                  const un = d.unresolved_count || 0;
                  const ok = d.already_valid_count || 0;
                  toast.success(
                    n > 0
                      ? `🖼 Repaired ${n} cover${n === 1 ? "" : "s"} · ${ok} already valid · ${un} unresolved`
                      : `No new covers to repair · ${ok} already valid · ${un} unresolved`,
                    { duration: 5000 },
                  );
                  // Phase 21.4 Batch 9 — expose the unresolved list so
                  // admins can see WHICH rows need manual attention.
                  if (un > 0 && Array.isArray(d.unresolved)) {
                    // eslint-disable-next-line no-console
                    console.warn("[HoF Repair] Unresolved rows:", d.unresolved);
                  }
                  loadRows();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Repair failed");
                } finally {
                  setRepairBusy(false);
                }
              }}
              disabled={repairBusy}
              variant="outline"
              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 text-xs px-3 py-1 h-auto"
              data-testid="admin-hof-repair-covers-btn"
            >
              {repairBusy ? "🔄 Repairing…" : "🖼 Repair covers"}
            </Button>
            <Button
              onClick={async () => {
                if (repairBadgesBusy) return;
                setRepairBadgesBusy(true);
                try {
                  const r = await api.post("/admin/hall-of-champions/repair-badges");
                  const d = r.data || {};
                  toast.success(
                    `🏅 Repaired badges: ${d.repaired_count || 0} · ` +
                    `${d.already_linked_count || 0} already linked · ` +
                    `${d.unresolved_count || 0} unresolved`,
                    { duration: 5000 },
                  );
                  loadRows();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Repair badges failed");
                } finally {
                  setRepairBadgesBusy(false);
                }
              }}
              disabled={repairBadgesBusy}
              variant="outline"
              className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10 text-xs px-3 py-1 h-auto"
              data-testid="admin-hof-repair-badges-btn"
            >
              {repairBadgesBusy ? "🔄 Linking…" : "🏅 Repair badges"}
            </Button>
            <Link to="/admin/contests" className="text-xs text-purple-300 hover:underline">← Back to contests</Link>
          </div>
        </div>

        {enabled === false && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" data-testid="admin-hof-flag-off">
            Contest Center feature flag is OFF — the public Hall of Champions endpoint returns nothing. Enable it via
            the Homepage Controls → Contests when you&apos;re ready to launch.
          </div>
        )}

        <div className="flex flex-wrap gap-2" data-testid="admin-hof-filters">
          <select
            value={year} onChange={(e) => setYear(e.target.value)}
            className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
            data-testid="admin-hof-filter-year"
          >
            <option value="" className="bg-[#13131a]">All years</option>
            {facets.years.map((y) => <option key={y} value={y} className="bg-[#13131a]">{y}</option>)}
          </select>
          <select
            value={contestId} onChange={(e) => setContestId(e.target.value)}
            className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm min-w-[200px]"
            data-testid="admin-hof-filter-contest"
          >
            <option value="" className="bg-[#13131a]">All contests</option>
            {facets.contests.map((c) => (
              <option key={c.contest_id} value={c.contest_id} className="bg-[#13131a]">
                {c.title || c.contest_id} ({c.count})
              </option>
            ))}
          </select>
          <select
            value={entryType} onChange={(e) => setEntryType(e.target.value)}
            className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
            data-testid="admin-hof-filter-entry-type"
          >
            <option value="" className="bg-[#13131a]">All types</option>
            {facets.entry_types.map((t) => (
              <option key={t.entry_type} value={t.entry_type} className="bg-[#13131a]">
                {t.entry_type} ({t.count})
              </option>
            ))}
          </select>
          <select
            value={position} onChange={(e) => setPosition(e.target.value)}
            className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
            data-testid="admin-hof-filter-position"
          >
            <option value="" className="bg-[#13131a]">All positions</option>
            <option value="1" className="bg-[#13131a]">🥇 1st</option>
            <option value="2" className="bg-[#13131a]">🥈 2nd</option>
            <option value="3" className="bg-[#13131a]">🥉 3rd</option>
          </select>
        </div>

        {/* Desktop table (hidden on mobile) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-hof-table">
            <thead className="text-left text-gray-400 text-xs uppercase tracking-wider border-b border-white/10">
              <tr>
                <th className="py-2 pr-3">Awarded</th>
                <th className="py-2 pr-3">Contest / Award</th>
                <th className="py-2 pr-3">Artist</th>
                <th className="py-2 pr-3">Placement</th>
                <th className="py-2 pr-3">Entry</th>
                <th className="py-2 pr-3">Badges</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-500 text-sm" data-testid="admin-hof-loading">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-500 text-sm" data-testid="admin-hof-empty">No champions yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.hof_id} className="border-b border-white/5" data-testid={`admin-hof-row-${r.hof_id}`}>
                  <td className="py-2 pr-3 text-xs text-gray-300">{r.awarded_at ? new Date(r.awarded_at).toLocaleDateString() : "—"}</td>
                  <td className="py-2 pr-3 text-white">{r.contest?.title || r.contest_title || r.category || "—"}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      {r.artist?.profile_photo && (
                        <img src={optimizeImage(r.artist.profile_photo, 'avatar')} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                      )}
                      <div>
                        <p className="text-white text-xs font-black">{r.artist?.artist_name || r.user_id}</p>
                        <p className="text-[10px] text-gray-500 font-mono">{r.user_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 pr-3 font-black text-amber-200" data-testid={`admin-hof-placement-${r.hof_id}`}>
                    {positionLabel(r.position)}
                  </td>
                  <td className="py-2 pr-3 text-gray-200">
                    <div className="flex items-center gap-2">
                      {/* iter_186 — prefer canonical iter_184 winner
                          cover URL; fallback to legacy snapshot. */}
                      {(r.winner_cover_url || r.entry_snapshot?.cover_art) && (
                        <img src={optimizeImage(r.winner_cover_url || r.entry_snapshot.cover_art, 'avatar')} alt="" className="w-10 h-10 rounded-md object-cover border border-white/10" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                      )}
                      <div>
                        <p>{r.entry_snapshot?.title || r.entry_ref_id || "—"}</p>
                        <p className="text-[10px] text-gray-500">{r.entry_type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.badge_details || []).map((b) => (
                        <span key={b.badge_id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-100 font-black inline-flex items-center gap-1">
                          {b.icon_url && <img src={b.icon_url} alt="" className="w-3 h-3 object-contain" />}
                          {b.label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    {r.backfilled && (
                      <span className="text-[10px] uppercase tracking-wider text-cyan-300/70 mr-2">manual</span>
                    )}
                    <button
                      onClick={() => startEdit(r)}
                      className="text-[11px] text-purple-300 hover:text-purple-100 font-black mr-3"
                      data-testid={`admin-hof-edit-${r.hof_id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteRow(r.hof_id)}
                      className="text-[11px] text-rose-300 hover:text-rose-100 font-black"
                      data-testid={`admin-hof-delete-${r.hof_id}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card layout (visible below md) */}
        <div className="md:hidden space-y-3" data-testid="admin-hof-cards-mobile">
          {loading && (
            <p className="text-center text-gray-500 text-sm py-6" data-testid="admin-hof-loading-mobile">Loading…</p>
          )}
          {!loading && rows.length === 0 && (
            <p className="text-center text-gray-500 text-sm py-6" data-testid="admin-hof-empty-mobile">No champions yet.</p>
          )}
          {rows.map((r) => (
            <div
              key={r.hof_id}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
              data-testid={`admin-hof-card-${r.hof_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                  {(r.winner_cover_url || r.entry_snapshot?.cover_art) ? (
                    <img src={optimizeImage(r.winner_cover_url || r.entry_snapshot.cover_art, 'card')} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  ) : r.artist?.profile_photo ? (
                    <img src={optimizeImage(r.artist.profile_photo, 'card')} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl">🎤</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-black truncate" data-testid={`admin-hof-card-artist-${r.hof_id}`}>
                    {r.artist?.artist_name || r.user_id}
                  </p>
                  <p className="text-[11px] text-gray-400 truncate">
                    {r.contest?.title || r.contest_title || r.category || "—"}
                  </p>
                  <p className="text-[10px] text-gray-500 truncate mt-0.5">
                    {r.entry_snapshot?.title || r.entry_ref_id || "—"}
                  </p>
                </div>
                <span className="text-lg font-black flex-shrink-0" data-testid={`admin-hof-card-placement-${r.hof_id}`}>
                  {positionLabel(r.position).split(" ")[0]}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(r.badge_details || []).map((b) => (
                  <span key={b.badge_id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-100 font-black inline-flex items-center gap-1">
                    {b.icon_url && <img src={b.icon_url} alt="" className="w-3 h-3 object-contain" />}
                    {b.label}
                  </span>
                ))}
                {r.backfilled && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 font-black uppercase tracking-wider">
                    manual
                  </span>
                )}
                {r.awarded_at && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full text-gray-400">
                    {new Date(r.awarded_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="mt-2 flex gap-3 text-[11px]">
                <button
                  onClick={() => startEdit(r)}
                  className="text-purple-300 hover:text-purple-100 font-black"
                  data-testid={`admin-hof-card-edit-${r.hof_id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteRow(r.hof_id)}
                  className="text-rose-300 hover:text-rose-100 font-black"
                  data-testid={`admin-hof-card-delete-${r.hof_id}`}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <HoFBackfillModal
        open={addOpen}
        onClose={() => { setAddOpen(false); setEditingRow(null); }}
        onCreated={() => { setEditingRow(null); loadRows(); }}
        initial={editingRow}
      />
      <HoFImportSpotlightModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => loadRows()}
      />
    </div>
  );
};

export default AdminHallOfChampionsPage;
